import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import DesktopWindow from '../../desktop/DesktopWindow';
import DesktopGlyph from '../../desktop/DesktopGlyph';
import UiIcon from '../../ui/UiIcon';
import LedgerApp from '../../desktop/LedgerApp';
import ConnectionsTerminal from '../../desktop/ConnectionsTerminal';
import { publicWallet, socialApps } from '../../../data/identityData';
import PaperTransition from '../PaperTransition/PaperTransition';
import { projects } from '../../../data/siteData';
import { desktopApps } from '../../../data/desktopApps';
import desktopBackground from '../../../assets/environment/desktop-wallpaper.svg';
import './DigitalShell.css';
import '../../desktop/DesktopApps.css';

const tabs = [
  { label: 'Overview', to: '/', match: (path) => path === '/' },
  { label: 'Projects', to: '/work', match: (path) => path.startsWith('/work') },
  { label: 'Blog', to: '/blog', match: (path) => path.startsWith('/blog') || path.startsWith('/thoughts') || path.startsWith('/database') },
  { label: 'Profile', to: '/about', match: (path) => path.startsWith('/about') },
  { label: 'Archive', to: '/archive', match: (path) => path.startsWith('/archive') },
];

const folderTransition = { duration: 0.28, ease: [0.22, 0.72, 0.16, 1] };
const taskbarApps = desktopApps.filter(app => ['ledger', 'connections'].includes(app.id));

const ArchiveShell = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();
  const tabsRef = useRef(null);
  const launcherRef = useRef(null);
  const documentRef = useRef(null);
  const lastFolderPathRef = useRef(location.pathname);
  const [opened, setOpened] = useState(location.pathname !== '/');
  const wasFolderOpenedRef = useRef(opened);
  const [closing, setClosing] = useState(false);
  const [appStates, setAppStates] = useState({});
  const [activeApp, setActiveApp] = useState(null);
  const [launcherOpen, setLauncherOpen] = useState(false);
  const [desktopTime, setDesktopTime] = useState(() => new Date());
  const clock = useMemo(() => ({
    time: new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', hourCycle: 'h23' }).format(desktopTime),
  }), [desktopTime]);
  const activeLabel = useMemo(() => tabs.find((tab) => tab.match(location.pathname))?.label || 'Record', [location.pathname]);
  const activeTab = tabs.find(tab => tab.match(location.pathname)) || tabs[0];
  const nestedFile = location.pathname !== activeTab.to;
  const recordTitle = location.pathname.startsWith('/work/') ? projects.find(project => location.pathname === `/work/${project.id}`)?.title : null;

  useEffect(() => {
    if (opened && !closing) lastFolderPathRef.current = location.pathname;
  }, [location.pathname, opened, closing]);

  useEffect(() => {
    if (location.pathname !== '/') {
      setOpened(true);
      setClosing(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    const activeTab = tabsRef.current?.querySelector('.is-active');
    if (activeTab && typeof tabsRef.current?.scrollTo === 'function') {
      tabsRef.current.scrollTo({ left: Math.max(0, activeTab.offsetLeft - 12), behavior: reduceMotion ? 'auto' : 'smooth' });
    }
  }, [location.pathname, opened, reduceMotion]);

  useEffect(() => {
    const clockTimer = window.setInterval(() => setDesktopTime(new Date()), 30000);
    return () => window.clearInterval(clockTimer);
  }, []);

  useEffect(() => {
    if (launcherOpen) launcherRef.current?.querySelector('[role="menuitem"]')?.focus();
  }, [launcherOpen]);

  useEffect(() => {
    if (documentRef.current) documentRef.current.scrollTop = 0;
  }, [location.pathname]);

  useEffect(() => {
    const wasOpened = wasFolderOpenedRef.current;
    wasFolderOpenedRef.current = opened;
    if (opened || !wasOpened) return undefined;
    // Wait for the restored taskbar to be committed before focusing it.
    const frame = window.requestAnimationFrame(() => document.querySelector('[data-app-id="files"]')?.focus({ preventScroll: true }));
    return () => window.cancelAnimationFrame(frame);
  }, [opened]);

  const focusTask = (id) => window.requestAnimationFrame(() => {
    const task = document.querySelector(`[data-app-id="${id}"]`);
    const target = task?.closest('[hidden]') ? document.getElementById('main-content') : task;
    target?.focus({ preventScroll: true });
  });

  const moveInLauncher = (event) => {
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const buttons = Array.from(launcherRef.current?.querySelectorAll('[role="menuitem"]') || []);
    const index = buttons.indexOf(document.activeElement);
    const next = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1 : (index + (event.key === 'ArrowDown' ? 1 : -1) + buttons.length) % buttons.length;
    buttons[next]?.focus();
  };

  const openFolder = () => {
    if (closing) return;
    navigate(lastFolderPathRef.current);
    setOpened(true);
  };

  const closeFolder = () => {
    if (closing) return;
    if (reduceMotion) {
      navigate('/');
      setOpened(false);
      return;
    }
    setClosing(true);
  };

  const finishClosing = () => {
    if (!closing) return;
    navigate('/');
    setOpened(false);
    setClosing(false);
  };

  const launchApp = (id) => {
    if (desktopApps.find(app => app.id === id)?.href) return;
    setAppStates((current) => ({ ...current, [id]: { visible: true } }));
    setActiveApp(id);
    setLauncherOpen(false);
  };
  const minimizeApp = (id) => {
    setAppStates((current) => ({ ...current, [id]: { visible: false } }));
    setActiveApp((current) => current === id ? null : current);
    focusTask(id);
  };
  const closeApp = (id) => {
    setAppStates((current) => { const next = { ...current }; delete next[id]; return next; });
    setActiveApp((current) => current === id ? null : current);
    focusTask(id);
  };

  const openDesktopFolder = () => {
    if (closing) return;
    setActiveApp(null);
    setLauncherOpen(false);
    openFolder();
    window.requestAnimationFrame(() => document.getElementById('main-content')?.focus({ preventScroll: true }));
  };
  const openPersonalFile = (path) => {
    lastFolderPathRef.current = path;
    setActiveApp(null);
    navigate(path);
    setOpened(true);
    window.requestAnimationFrame(() => document.getElementById('main-content')?.focus({ preventScroll: true }));
  };
  const toggleApp = (id) => activeApp === id && appStates[id]?.visible ? minimizeApp(id) : launchApp(id);
  const terminalFiles = [
    { id: 'files', label: 'Files', aliases: ['folder'], kind: 'file', path: '/', description: 'The personal folder: overview, projects, blog, profile, and archive.' },
    ...tabs.map(tab => ({ id: `file:${tab.label.toLowerCase()}`, label: tab.label, kind: 'file', path: tab.to, description: `The ${tab.label.toLowerCase()} section of the personal folder.` })),
  ];
  const terminalDestinations = [
    ...desktopApps.filter(app => app.id !== 'connections').map(app => ({ id: app.id, label: app.title, aliases: app.aliases || [], kind: app.href ? 'shortcut' : 'app', href: app.href, description: app.project?.summary || app.description, repository: app.project?.link?.startsWith('https://github.com/') ? app.project.link : undefined })),
    ...terminalFiles,
  ];
  const openFromTerminal = id => {
    const file = terminalFiles.find(item => item.id === id);
    if (!file) { launchApp(id); return; }
    lastFolderPathRef.current = file.path;
    setActiveApp(null);
    navigate(file.path);
    setOpened(true);
    window.requestAnimationFrame(() => document.getElementById('main-content')?.focus({ preventScroll: true }));
  };

  const appIcon = app => <DesktopGlyph type={app.icon} mark={app.mark} src={app.iconSrc} />;
  const appShortcut = (app, surface) => {
    const label = surface === 'desktop' ? `Launch ${app.title}` : surface === 'taskbar' ? `Open ${app.title}` : undefined;
    const content = surface === 'desktop' ? <>{appIcon(app)}<strong>{app.title}</strong><small>{app.description}</small></> : <>{appIcon(app)}<span>{app.title}</span></>;
    const shared = { 'aria-label': label, 'data-app-id': app.id, title: app.title, role: surface === 'launcher' ? 'menuitem' : undefined };
    return app.href
      ? <a key={app.id} {...shared} href={app.href} target="_blank" rel="noopener noreferrer" onClick={() => setLauncherOpen(false)}>{content}</a>
      : <button key={app.id} {...shared} type="button" onClick={() => surface === 'taskbar' ? toggleApp(app.id) : launchApp(app.id)} aria-pressed={surface === 'taskbar' ? activeApp === app.id : undefined} className={appStates[app.id] ? 'is-running' : ''}>{content}</button>;
  };

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key !== 'Escape') return;
      if (launcherOpen) { setLauncherOpen(false); focusTask('launcher'); }
      else if (activeApp) minimizeApp(activeApp);
      else if (opened && !closing) closeFolder();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  });

  return (
    <div className={`archive-environment ${opened ? 'is-open' : 'is-closed'} ${closing ? 'is-closing' : ''} ${activeApp ? 'has-app-focus' : 'has-file-focus'}`} style={{ '--desktop-background': `url(${desktopBackground})` }} onPointerDown={(event) => { if (!event.target.closest('.desktop-launcher, .desktop-start')) setLauncherOpen(false); }}>
      {!opened && !closing && <nav className="desktop-shortcuts" aria-label="Desktop apps"><button type="button" onClick={openDesktopFolder} aria-label="Open Ty0x7 public record"><DesktopGlyph type="folder" mark="TY" /><strong>Ty0x7</strong><small>Personal files</small></button>{desktopApps.filter(app => app.desktop !== false).map(app => appShortcut(app, 'desktop'))}</nav>}
      {(opened || closing) && <motion.section className="archive-folder" aria-label="Ty0x7 personal archive" onPointerDownCapture={() => setActiveApp(null)} onFocusCapture={() => setActiveApp(null)} initial={reduceMotion ? false : { opacity: 0, scale: .98 }} animate={{ opacity: closing ? 0 : 1, scale: closing ? .98 : 1 }} transition={reduceMotion ? { duration: 0 } : folderTransition} onAnimationComplete={finishClosing}>
        <div className="folder-back" aria-hidden={!opened}>
          <div className="folder-back__meta"><span>TY0X7 / PERSONAL FILES</span></div>
          {opened && <nav ref={tabsRef} className="folder-tabs" aria-label="Public record sections">
            {tabs.map((tab, index) => <NavLink to={tab.to} end={tab.to === '/'} className={tab.match(location.pathname) ? 'is-active' : ''} key={tab.label}><span>0{index + 1}</span>{tab.label}</NavLink>)}
          </nav>}
        </div>

        {(opened || closing) && <motion.div className="folder-contents" animate={{ opacity: closing ? 0 : 1 }} transition={{ duration: reduceMotion ? 0 : 0.28, ease: 'easeOut' }}>
          <div className="record-toolbar"><nav className="folder-breadcrumbs" aria-label="Folder path"><button type="button" aria-label="Up one folder" disabled={location.pathname === '/'} onClick={() => openPersonalFile(nestedFile ? activeTab.to : '/')}><UiIcon name="up" /></button><DesktopGlyph type="folder" /><Link to="/">Ty0x7</Link>{location.pathname !== '/' && <><i>/</i>{nestedFile ? <Link to={activeTab.to}>{activeLabel}</Link> : <span>{activeLabel}</span>}</>}{nestedFile && <><i>/</i><span className="folder-current-record">{recordTitle || 'Record'}</span></>}</nav><button type="button" className="folder-close" aria-label="Close file" onClick={closeFolder}><span>Close file</span><UiIcon name="close" /></button></div>
          <main ref={documentRef} id="main-content" className="active-document" tabIndex={0} aria-label={`${activeLabel} file`}>
            <PaperTransition
              className="document-page"
              recordKey={location.pathname}
            >
              {children}
            </PaperTransition>
          </main>
          <footer className="folder-status"><span>{activeLabel === 'Projects' ? `${projects.filter(project => !project.hidden).length} project records` : `${activeLabel} / personal files`}</span><button type="button" onClick={() => documentRef.current?.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' })}>Back to top <UiIcon name="up" /></button></footer>
        </motion.div>}
      </motion.section>}
      {desktopApps.filter((app) => !app.href && appStates[app.id]).map((app) => <DesktopWindow key={app.id} title={app.title} icon={app.icon} mark={app.mark} iconSrc={app.iconSrc} active={activeApp === app.id} visible={appStates[app.id].visible} onActivate={() => setActiveApp(app.id)} onMinimize={() => minimizeApp(app.id)} onClose={() => closeApp(app.id)}>
        {app.id === 'ledger' ? <LedgerApp wallet={publicWallet} active={activeApp === app.id && appStates[app.id].visible} /> : <ConnectionsTerminal apps={socialApps} destinations={terminalDestinations} onOpen={openFromTerminal} onClose={() => closeApp(app.id)} active={activeApp === app.id && appStates[app.id].visible} />}
      </DesktopWindow>)}
      {launcherOpen && !opened && !closing && <div ref={launcherRef} className="desktop-launcher" role="menu" aria-label="Application launcher" onKeyDown={moveInLauncher}><span>TY0X7 / Applications</span><button type="button" role="menuitem" onClick={openDesktopFolder}><DesktopGlyph type="folder" />Personal files</button>{desktopApps.map(app => appShortcut(app, 'launcher'))}</div>}
      <nav className="desktop-taskbar" aria-label="Desktop applications" hidden={opened || closing}>
        <button type="button" data-app-id="launcher" className="desktop-start" onClick={() => setLauncherOpen((current) => !current)} aria-label="Applications" aria-expanded={launcherOpen} aria-haspopup="menu"><UiIcon name="apps" /><span>Apps</span></button>
        <div className="desktop-taskbar__apps"><button type="button" data-app-id="files" onClick={openDesktopFolder} aria-label="Show personal folder" aria-pressed={opened && !activeApp} disabled={closing}><DesktopGlyph type="folder" /><span>Files</span></button>{taskbarApps.map(app => appShortcut(app, 'taskbar'))}</div>
        <span className="desktop-taskbar__status"><time>{clock.time}</time></span>
      </nav>
    </div>
  );
};

export default ArchiveShell;
